import './ShipmentToolbar.css';
import { aiHistoryMassActions } from '@/apis/aiHistory.js';
import { DownloadCOC, GetCOCExportCount } from '@/apis/shipment.js';
import theme from '@/assets/theme.js';
import { useFeaturePermissions, useUnAuthorize } from '@/hooks/index.js';
import { FeatureLabels } from '@/common/enum';
import { themeT } from '@/utils/themeText.js';
import { message, Tooltip } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import CheckCircle from '../../../assets/images/CheckCircle';
import ClearAll from '../../../assets/images/ClearAll.jsx';
import MultiCheck from '../../../assets/images/MultiCheck.jsx';
import SelectAll from '../../../assets/images/SelectAll.jsx';
import useToken from '../../../common/helper/useToken';
import CustomParagraph from '../../../components/CustomParagraph.jsx';
import CustomDeleteModal from '../../Modals/CustomDeleteModal.jsx';
import { getCOCModalMessage } from '../components/COCModalMessage.js';
import { saveBlobAsFile } from '../components/fileDownload.js';
import MarkAsDeliveredModal from '../ControlTower/MarkAsDeliveredModal.js';
import PerShipmentNotificationModal from '../PerShipmentNotification/PerShipmentNotificationModal.jsx';
import { useMassAction } from './useMassAction.jsx';
import {
  getShipmentToolbarCountText,
  getToolbarSelectedCount,
  splitCalendarScopedFilters,
} from './utils';

import FloatingToolbar from './FloatingToolbar.tsx';
import { CustomModalWithoutButton } from '@/components';
import BulkShareTrackingLinks from '../../Modals/BulkShareTrackingLinks.tsx';
import EditTeamsModal from './EditTeamsModal.tsx';
import { useSelector } from 'react-redux';

const ShipmentToolbar = ({
  onApplyQuickFilter,
  selected = [],
  overAllShipmentCount = 0,
  isSearchTriggered,
  appliedFilters,
  setSelectedShipmentStatuses,
  searchQuery,
  selectedShipmentStatuses,
  filteredData = [],
  setFilteredData,
  totalShipments,
  setShipmentsData,
  fetchShipments,
  page,
  pageSize,
  checked,
  setChecked,
  selectedIds,
  setSelectedIds,
  isAiHistory = false,
  isCalendarActive = false,
  getViewportCalendarIds,
  selectableItems = [],
  refreshCurrentView,
  aiHistoryAlertIds = [],
  aiHistoryEventIds = [],
  hasAiHistoryResolvedSelected = false,
  hasAiHistoryDismissedSelected = false,
  // still passed by the shipments pages; dismiss alerts now reads the alerts feature
  CT_all_permissions = {},
  selectAllAcrossFilters = false,
  setSelectAllAcrossFilters = (_value) => {},
  appliedSearchQuery = '',
  filtersPayload = {},
  excludedIds = [],
  setExcludedIds = (_value) => {},
  isDrawerOpen = false,
  activeTabId = '',
  selectionScopeCount = null,
  toggleDrawer = (_action, _type, _payload) => {},
  legType = 'CL',
}) => {
  const teamsList = useSelector((state) => state.teams?.list || []);
  const recordManagementPermissions = useFeaturePermissions(
    FeatureLabels.RecordManagement
  );
  const alertsPermissions = useFeaturePermissions(FeatureLabels.Alerts);
  const hasValidTeams = !(
    teamsList.length === 0 ||
    (teamsList.length === 1 && teamsList[0]?.name?.toLowerCase() === 'default')
  );

  const { token, decodedUserToken } = useToken();
  const [showDeliveredModal, setShowDeliveredModal] = useState(false);
  const { unauthorize } = useUnAuthorize();
  const user = decodedUserToken();
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [archiveModalVisible, setArchiveModalVisible] = useState(false);
  const [deliverModalVisible, setDeliverModalVisible] = useState(false);
  const { handleMassAction } = useMassAction();
  const [openNotificationModal, setOpenNotificationModal] = useState(false);
  const [notificationType, setNotificationType] = useState(1);
  const [notificationBulkScope, setNotificationBulkScope] = useState(null);
  // snapshot so parent re-renders don't hand the modal a new array and refetch recipients
  const [notificationShipmentIds, setNotificationShipmentIds] = useState([]);
  const [showCOCConfirmationModal, setShowCOCConfirmationModal] =
    useState(false);
  const [cOCExportCounts, setCOCExportCounts] = useState({});
  const [exportItemsState, setExportItemsState] = useState({});
  const [mainMenuVisible, setMainMenuVisible] = useState(false);
  const [exportCocDropdownVisible, setExportCocDropdownVisible] =
    useState(false);
  const [editTeamsModalVisible, setEditTeamsModalVisible] = useState(false);
  const [shareLinksModalVisible, setShareLinksModalVisible] = useState(false);

  const itemLabel = isAiHistory ? 'row' : 'shipment';
  const itemLabelPlural = isAiHistory ? 'rows' : 'shipments';
  const getLabel = (count) => (count === 1 ? itemLabel : itemLabelPlural);

  const useSelectAllMode = !!selectAllAcrossFilters;

  const getAiHistoryPayload = useCallback(
    (isResolved) => {
      const basePayload = {
        alert_ids: aiHistoryAlertIds ?? [],
        event_ids: aiHistoryEventIds ?? [],
        is_resolved: isResolved,
      };

      if (useSelectAllMode) {
        return {
          ...basePayload,
          select_all: true,
          exclude_shipment_ids: excludedIds ?? [],
          filters: {
            ...(filtersPayload && typeof filtersPayload === 'object'
              ? filtersPayload
              : {}),
            search: (appliedSearchQuery ?? '').trim(),
          },
        };
      }

      return basePayload;
    },
    [
      useSelectAllMode,
      excludedIds,
      filtersPayload,
      appliedSearchQuery,
      aiHistoryAlertIds,
      aiHistoryEventIds,
    ]
  );

  const getAiHistoryCount = useCallback(() => {
    if (useSelectAllMode) {
      return Math.max(0, totalShipments - excludedIds.length);
    }

    const payload = getAiHistoryPayload(true);
    return (payload.alert_ids?.length ?? 0) + (payload.event_ids?.length ?? 0);
  }, [getAiHistoryPayload, useSelectAllMode, totalShipments, excludedIds]);

  const refresh = useCallback(() => {
    if (typeof refreshCurrentView === 'function') {
      refreshCurrentView();
      return;
    }

    if (typeof fetchShipments !== 'function') return;

    if (isAiHistory) {
      fetchShipments(page, searchQuery);
      return;
    }

    fetchShipments(searchQuery, false, page, pageSize, null);
  }, [
    refreshCurrentView,
    fetchShipments,
    isAiHistory,
    page,
    searchQuery,
    pageSize,
  ]);

  const getIdsInContext = useCallback(() => {
    if (isCalendarActive) {
      const viewportIds = (getViewportCalendarIds?.() ?? [])
        .map((x) => String(x))
        .filter(Boolean);
      if (viewportIds.length > 0) return viewportIds;

      return (selectableItems ?? []).map((x) => String(x.id)).filter(Boolean);
    }

    return (filteredData ?? []).map((x) => String(x.id)).filter(Boolean);
  }, [isCalendarActive, getViewportCalendarIds, filteredData, selectableItems]);

  const idsInContext = useMemo(() => getIdsInContext(), [getIdsInContext]);

  const notificationSelectedIds = useMemo(() => {
    if (useSelectAllMode) {
      return idsInContext.filter((id) => !excludedIds.includes(id));
    }

    return idsInContext.filter((id) => selectedIds.includes(id));
  }, [idsInContext, useSelectAllMode, excludedIds, selectedIds]);

  useEffect(() => {
    if (!checked) {
      setSelectedIds([]);
      setSelectedShipmentStatuses({});
      setExcludedIds([]);
      if (
        selectAllAcrossFilters &&
        typeof setSelectAllAcrossFilters === 'function'
      ) {
        setSelectAllAcrossFilters(false);
      }
    }
  }, [
    checked,
    setSelectedIds,
    setSelectedShipmentStatuses,
    setExcludedIds,
    selectAllAcrossFilters,
    setSelectAllAcrossFilters,
  ]);

  const handleOpenNotification = (val) => {
    if (notificationSelectedIds.length === 0) {
      message.error(`Please select at least one ${itemLabel}`);
      return;
    }

    const hasDelivered = notificationSelectedIds.some(
      (id) => selectedShipmentStatuses?.[id] === 'DELIVERED'
    );

    if (hasDelivered) {
      message.error(
        'You cannot add a notification to delivered shipments. One or more selected shipments are already marked as delivered.'
      );
      return;
    }

    // only calendar select-all sends scope keys; visible ids there are just a subset
    if (isCalendarActive && useSelectAllMode) {
      const { filters, start, end, view_by } =
        splitCalendarScopedFilters(filtersPayload);
      setNotificationBulkScope({
        select_all: true,
        exclude_shipment_ids: excludedIds,
        filters: {
          ...filters,
          search: (appliedSearchQuery ?? '').trim(),
        },
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
        ...(view_by ? { view_by } : {}),
      });
    } else {
      setNotificationBulkScope(null);
    }

    setNotificationShipmentIds(notificationSelectedIds);
    setOpenNotificationModal(true);
    setNotificationType(val);
  };

  // Only loaded shipments have a reference ID to label their link with.
  const shareTrackingShipments = useMemo(() => {
    const rowsById = new Map(
      [...(selectableItems ?? []), ...(filteredData ?? [])].map((row) => [
        String(row.id),
        row,
      ])
    );
    return notificationSelectedIds.map((id) => ({
      id,
      reference_number: rowsById.get(id)?.reference_number ?? null,
    }));
  }, [notificationSelectedIds, selectableItems, filteredData]);

  const handleOpenShareTrackingLinks = () => {
    if (shareTrackingShipments.length === 0) {
      message.error(`Please select at least one ${itemLabel}`);
      return;
    }
    setShareLinksModalVisible(true);
  };

  const handleOpenProductDrawer = () => {
    if (notificationSelectedIds.length === 0) {
      message.error(`Please select at least one ${itemLabel}`);
      return;
    }

    const { filters, start, end, view_by } =
      splitCalendarScopedFilters(filtersPayload);
    const payload = {
      select_all: useSelectAllMode,
      exclude_shipment_ids: excludedIds,
      shipment_ids: selectedIds ?? [],
      filters: {
        ...filters,
        search: (appliedSearchQuery ?? '').trim(),
      },
      ...(start ? { start } : {}),
      ...(end ? { end } : {}),
      ...(view_by ? { view_by } : {}),
    };
    const payloadMeta = JSON.stringify(payload);
    toggleDrawer(
      'open',
      'shipmentProductsList',
      payloadMeta,
      undefined,
      legType
    );
  };

  const handleSelectAll = () => {
    const next = !selectAllAcrossFilters;

    if (typeof setSelectAllAcrossFilters === 'function') {
      setSelectAllAcrossFilters(next);
    }

    if (!checked) setChecked(true);

    if (!next) {
      setSelectedIds([]);
      setSelectedShipmentStatuses({});
      setExcludedIds([]);
      return;
    }

    setSelectedIds([]);
    setExcludedIds([]);
  };

  const resetSelection = useCallback(() => {
    setSelectedIds([]);
    setSelectedShipmentStatuses({});
    setExcludedIds([]);
    setSelectAllAcrossFilters(false);
  }, [
    setSelectedIds,
    setSelectedShipmentStatuses,
    setExcludedIds,
    setSelectAllAcrossFilters,
  ]);

  const handleConfirmDismiss = () => {
    const idsToDismiss = [...selectedIds];
    setDeleteModalVisible(false);

    if (isAiHistory) {
      if (!useSelectAllMode && hasAiHistoryDismissedSelected) {
        message.error('One or more selected rows are already dismissed.');
        return;
      }

      const count = getAiHistoryCount();
      if (count === 0) {
        message.error('No alerts/events found for selected rows');
        return;
      }

      handleMassAction({
        action: 'dismiss_all_alerts',
        shipmentIds: idsToDismiss,
        token,
        successMessage: 'Status updated',
        errorMessage: 'Dismiss failed',
        entityLabel: 'row',
        execute: aiHistoryMassActions,
        countForUndo: count,
        buildPayload: () => getAiHistoryPayload(false),
        onSuccess: () => {
          resetSelection();
          refresh();
        },
      });

      return;
    }

    handleMassAction({
      action: 'dismiss_all_alerts',
      shipmentIds: idsToDismiss,
      token,
      successMessage: 'Status updated',
      errorMessage: 'Dismiss failed',
      entityLabel: 'shipment',
      selectAll: useSelectAllMode,
      filtersPayload,
      search: (appliedSearchQuery ?? '').trim(),
      excludeShipmentIds: useSelectAllMode ? excludedIds : [],
      countForUndo: selectedCount,
      onSuccess: () => {
        resetSelection();
        refresh();
      },
    });
  };

  const handleConfirmArchive = () => {
    setArchiveModalVisible(false);
    const prevData = [...filteredData];
    const idsToArchive = [...selectedIds];

    if (!useSelectAllMode) {
      const updatedData = filteredData
        .map((item) =>
          selectedIds.includes(item.id)
            ? { ...item, status: 'ARCHIVED', type: 4 }
            : item
        )
        .filter((item) => item.status !== 'ARCHIVED');

      setFilteredData(updatedData);
      setShipmentsData(updatedData);
    }

    handleMassAction({
      action: 'archive',
      shipmentIds: idsToArchive,
      token,
      successMessage: 'Status updated',
      errorMessage: 'Could not archive shipments',
      selectAll: useSelectAllMode,
      filtersPayload,
      search: (appliedSearchQuery ?? '').trim(),
      excludeShipmentIds: useSelectAllMode ? excludedIds : [],
      countForUndo: selectedCount,
      onUndo: () => {
        if (!useSelectAllMode) {
          setFilteredData(prevData);
          setShipmentsData(prevData);
        }
      },
      onSuccess: () => {
        resetSelection();
        refresh();
      },
    });
  };

  const handleConfirmDelivered = (model) => {
    const prevData = [...filteredData];
    const idsToDeliver = [...selectedIds];

    setDeliverModalVisible(false);

    if (isAiHistory) {
      if (!useSelectAllMode && hasAiHistoryResolvedSelected) {
        message.error('One or more selected rows are already resolved.');
        return;
      }

      const count = getAiHistoryCount();
      if (count === 0) {
        message.error('No alerts/events found for selected rows');
        return;
      }

      handleMassAction({
        action: 'mark_as_resolved',
        shipmentIds: idsToDeliver,
        token,
        successMessage: 'Status updated',
        errorMessage: 'Error updating status',
        entityLabel: 'row',
        execute: aiHistoryMassActions,
        countForUndo: count,
        buildPayload: () => getAiHistoryPayload(true),
        onSuccess: () => {
          resetSelection();
          setShowDeliveredModal(false);
          refresh();
        },
        onUndo: () => {
          setFilteredData(prevData);
        },
      });

      return;
    }

    if (!useSelectAllMode) {
      const hasDelivered = selectedIds.some(
        (id) => selectedShipmentStatuses?.[id] === 'DELIVERED'
      );

      if (hasDelivered) {
        message.error('One or more selected shipments are already delivered.');
        return;
      }
    }

    handleMassAction({
      buildPayload: () => model,
      action: 'mark_as_delivered',
      shipmentIds: idsToDeliver,
      token,
      successMessage: 'Status updated',
      errorMessage: 'Error updating status',
      entityLabel: 'shipment',
      selectAll: useSelectAllMode,
      filtersPayload,
      search: (appliedSearchQuery ?? '').trim(),
      excludeShipmentIds: useSelectAllMode ? excludedIds : [],
      countForUndo: selectedCount,
      onUndo: () => {
        if (!useSelectAllMode) {
          setFilteredData(prevData);
          setShipmentsData(prevData);
        }
      },
      onSuccess: () => {
        resetSelection();
        refresh();
      },
    });
  };

  const handleClickExportCOC = ({ exportOptions }) => {
    setExportItemsState(exportOptions);
    setShowCOCConfirmationModal(true);
    // Close the export dropdown when opening confirmation modal
    setExportCocDropdownVisible(false);
  };

  const fetchCOCCount = useCallback(async () => {
    const model = useSelectAllMode
      ? (() => {
          const { filters, start, end, view_by } =
            splitCalendarScopedFilters(filtersPayload);
          return {
            select_all: true,
            exclude_shipment_ids: excludedIds,
            filters: {
              ...filters,
              search: (appliedSearchQuery ?? '').trim(),
            },
            ...(start ? { start } : {}),
            ...(end ? { end } : {}),
            ...(view_by ? { view_by } : {}),
          };
        })()
      : {
          shipment_ids: selectedIds,
        };

    GetCOCExportCount(token, model)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data?.is_success) {
            setCOCExportCounts(data?.data?.data || {});
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      });
  }, [
    token,
    unauthorize,
    selectedIds,
    useSelectAllMode,
    excludedIds,
    filtersPayload,
    appliedSearchQuery,
  ]);

  const finalCount =
    (exportItemsState?.statusUpdates ? cOCExportCounts?.status_pdf_count : 0) +
    (exportItemsState?.pods ? cOCExportCounts?.pod_pdf_count : 0);

  const handleExportCOC = async () => {
    const isEmail = finalCount > 200;
    message.success(
      `${
        theme.themeType === 2 ? 'Transit history' : 'Chain of custody'
      } export started for ${cOCExportCounts?.selected_shipments} ${
        cOCExportCounts?.selected_shipments === 1 ? 'shipment' : 'shipments'
      }`
    );

    try {
      const baseModel = {
        shipment_ids: selectedIds,
        email: isEmail,
        include_status_updates: exportItemsState?.statusUpdates || false,
        include_comments: exportItemsState?.comments || false,
        include_alerts: exportItemsState?.alerts || false,
        include_pods: exportItemsState?.pods || false,
      };

      const model = useSelectAllMode
        ? (() => {
            const { filters, start, end, view_by } =
              splitCalendarScopedFilters(filtersPayload);
            return {
              ...baseModel,
              select_all: true,
              exclude_shipment_ids: excludedIds,
              filters: {
                ...filters,
                search: (appliedSearchQuery ?? '').trim(),
              },
              ...(start ? { start } : {}),
              ...(end ? { end } : {}),
              ...(view_by ? { view_by } : {}),
            };
          })()
        : baseModel;

      const res = await DownloadCOC(token, model);
      setShowCOCConfirmationModal(false);

      if (res?.status !== 202) {
        const zipName =
          theme.themeType === 2 ? 'Transit_history' : 'Chain_of_custody';
        saveBlobAsFile(
          res,
          `${zipName}_export_${new Date().toISOString().slice(0, 10)}.zip`
        );
      }

      // Don't reset selection immediately to avoid affecting menu states
      // resetSelection();

      if (isEmail) {
        message.success(`We’ll email you a download link when it’s ready`);
      }
    } catch (err) {
      console.error(err);
      message.error(
        `We couldn’t prepare your ${themeT(
          'chainOfCustody',
          theme.themeType
        )?.toLowerCase()} export. Please try again or contact support.`
      );
      unauthorize(err);
    }
  };

  const selectedCount = useMemo(() => {
    return getToolbarSelectedCount({
      checked,
      useSelectAllMode,
      isAiHistory,
      currentContextCount: idsInContext.length,
      selectionScopeCount,
      totalShipments,
      overAllShipmentCount,
      excludedIdsLength: excludedIds?.length ?? 0,
      selectedIdsLength: selectedIds?.length ?? 0,
    });
  }, [
    checked,
    useSelectAllMode,
    isAiHistory,
    idsInContext,
    selectionScopeCount,
    totalShipments,
    overAllShipmentCount,
    excludedIds,
    selectedIds,
  ]);

  const selectedCountText = `${selectedCount} selected`;

  const handleMissingPODsClick = () => {
    if (!selectedIds?.length && !useSelectAllMode) {
      message.error('Please select at least one shipment');
      return;
    }

    onApplyQuickFilter?.({
      filterBy: 'missing_pod',
      groupTitle: 'POD',
      items: [
        { id: 'missing_pod', name: 'Missing PODs', title: 'Missing PODs' },
      ],
      append: false,
      filterValueOverride: cOCExportCounts.shipment_ids_with_missing_pod,
    });
  };

  return (
    <div>
      <div className='shipment-toolbar-controls'>
        <MultiCheck
          activeColor={
            isAiHistory ? 'var(--text-secondary)' : 'var(--gray-600)'
          }
          active={checked}
          onClick={() => setChecked(!checked)}
        />

        {!checked && <div className='shipment-toolbar-divider' />}

        <CustomParagraph
          text={getShipmentToolbarCountText({
            checked,
            selectedCountText: '',
            totalShipments,
            appliedFilters,
            isSearchTriggered,
            selected,
            activeTabId,
            getLabel,
          })}
          fontSize='12px'
          fontWeight='400'
          lineHeight='20px'
          marginBottom={0}
          className='toolbar-filterd-text'
        />

        {checked && (
          <>
            {isAiHistory && (
              <Tooltip title='Select All'>
                <div
                  onClick={handleSelectAll}
                  className='shipment-toolbar-icon'
                >
                  <SelectAll color='var(--text-primary)' />
                </div>
              </Tooltip>
            )}
            {/* mark as Resolved / delivered  */}
            {isAiHistory && (
              <Tooltip
                title={`Mark as ${isAiHistory ? 'Resolved' : 'Delivered'}  `}
              >
                <div
                  onClick={() => {
                    if (!useSelectAllMode && selectedIds.length === 0) {
                      message.error(`Please select at least one ${itemLabel}`);
                    } else {
                      setDeliverModalVisible(true);
                    }
                  }}
                  className='shipment-toolbar-icon'
                >
                  <CheckCircle color='var(--text-primary)' />
                </div>
              </Tooltip>
            )}
            {/* Dismiss Alerts */}
            {isAiHistory && (
              <Tooltip title={isAiHistory ? 'Dismiss' : 'Dismiss Alerts'}>
                <div
                  onClick={() => {
                    if (!useSelectAllMode && selectedIds.length === 0) {
                      message.error(
                        isAiHistory
                          ? 'Please select at least one row to dismiss'
                          : 'Please select at least one shipment to dismiss alerts'
                      );
                    } else {
                      setDeleteModalVisible(true);
                    }
                  }}
                  className='shipment-toolbar-icon'
                >
                  <ClearAll color='var(--text-primary)' />
                </div>
              </Tooltip>
            )}
          </>
        )}
        {/* // new bottom toolbar  */}
        {!isAiHistory && (checked || selectAllAcrossFilters) && (
          <FloatingToolbar
            checked={checked}
            setChecked={setChecked}
            selectedCountText={selectedCountText}
            totalShipments={totalShipments}
            appliedFilters={appliedFilters}
            isSearchTriggered={isSearchTriggered}
            selected={selected}
            activeTabId={activeTabId}
            getLabel={getLabel}
            handleSelectAll={handleSelectAll}
            useSelectAllMode={useSelectAllMode}
            selectedIds={selectedIds}
            allPermissions={recordManagementPermissions}
            alertsPermissions={alertsPermissions}
            isAiHistory={isAiHistory}
            itemLabel={itemLabel}
            setShowDeliveredModal={setShowDeliveredModal}
            archiveModalVisible={archiveModalVisible}
            setArchiveModalVisible={setArchiveModalVisible}
            handleClickExportCOC={handleClickExportCOC}
            fetchCOCCount={fetchCOCCount}
            cOCExportCounts={cOCExportCounts}
            exportCocDropdownVisible={exportCocDropdownVisible}
            setExportCocDropdownVisible={setExportCocDropdownVisible}
            mainMenuVisible={mainMenuVisible}
            setMainMenuVisible={setMainMenuVisible}
            handleMissingPODsClick={handleMissingPODsClick}
            handleOpenNotification={handleOpenNotification}
            handleOpenProductDrawer={handleOpenProductDrawer}
            setDeleteModalVisible={setDeleteModalVisible}
            setEditTeamsModalVisible={setEditTeamsModalVisible}
            canShareTrackingLinks={Boolean(CT_all_permissions?.is_update)}
            handleOpenShareTrackingLinks={handleOpenShareTrackingLinks}
            hasValidTeams={hasValidTeams}
            isDrawerOpen={isDrawerOpen}
            legType={legType}
          />
        )}

        <div className='shipment-toolbar-divider' />
      </div>

      <CustomDeleteModal
        visible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onConfirm={handleConfirmDismiss}
        title={isAiHistory ? 'Dismiss' : 'Dismiss all alerts'}
        message={
          isAiHistory
            ? `You’re about to dismiss ${selectedCount} row${
                selectedCount !== 1 ? 's' : ''
              }.`
            : `You’re about to dismiss all alerts for ${selectedCount} shipment${
                selectedCount !== 1 ? 's' : ''
              }.`
        }
        cancelText='Cancel'
        confirmText={isAiHistory ? 'Dismiss' : 'Dismiss all alerts'}
        confirmColor='var(--danger)'
      />

      <CustomDeleteModal
        visible={archiveModalVisible}
        onClose={() => setArchiveModalVisible(false)}
        onConfirm={handleConfirmArchive}
        title='Archive'
        message={`You're about to archive ${selectedCount} shipment${
          selectedCount !== 1 ? 's' : ''
        }.`}
        cancelText='Cancel'
        confirmText='Archive'
        confirmColor='var(--danger)'
      />

      <CustomDeleteModal
        visible={deliverModalVisible}
        onClose={() => setDeliverModalVisible(false)}
        onConfirm={handleConfirmDelivered}
        title={isAiHistory ? 'Mark as Resolved' : 'Mark as Delivered'}
        message={
          isAiHistory
            ? `You're about to mark ${selectedCount} row${
                selectedCount !== 1 ? 's' : ''
              } as resolved.`
            : `You're about to mark ${selectedCount} shipment${
                selectedCount !== 1 ? 's' : ''
              } as delivered.`
        }
        cancelText='Cancel'
        confirmText={isAiHistory ? 'Mark as resolved' : 'Mark as delivered'}
        confirmColor='var(--danger)'
      />

      {openNotificationModal && (
        <PerShipmentNotificationModal
          selected={null}
          fetchNotifications={() => {}}
          shipmentId={notificationShipmentIds}
          notificationModalType={notificationType}
          openNotificationModal={openNotificationModal}
          setOpenNotificationModal={setOpenNotificationModal}
          isMultiple={true}
          bulkScope={notificationBulkScope}
        />
      )}

      {shareLinksModalVisible && (
        <CustomModalWithoutButton
          modalOpen={shareLinksModalVisible}
          setModalOpen={setShareLinksModalVisible}
          modalTitle='Share shipments'
          modalText=''
          modalSize={524}
        >
          <BulkShareTrackingLinks
            shipments={shareTrackingShipments}
            token={token}
            selectedCount={selectedCount}
          />
        </CustomModalWithoutButton>
      )}

      {showDeliveredModal && (
        <MarkAsDeliveredModal
          shipmentData={null}
          selectedIds={selectedIds}
          selectAllAcrossFilters={selectAllAcrossFilters}
          excludedIds={excludedIds}
          filtersPayload={filtersPayload}
          appliedSearchQuery={appliedSearchQuery}
          type='multiple'
          handleConfirmDelivered={handleConfirmDelivered}
          open={showDeliveredModal}
          onCancel={() => setShowDeliveredModal(false)}
        />
      )}

      <EditTeamsModal
        open={editTeamsModalVisible}
        onCancel={() => setEditTeamsModalVisible(false)}
        selectedIds={selectedIds}
        selectedCount={selectedCount}
        selectAllAcrossFilters={useSelectAllMode}
        excludedIds={excludedIds}
        filtersPayload={filtersPayload}
        appliedSearchQuery={appliedSearchQuery}
        onApply={({ add_team_ids, remove_team_ids }) => {
          setEditTeamsModalVisible(false);
          handleMassAction({
            action: 'update_teams',
            shipmentIds: selectedIds,
            token,
            successMessage: 'Teams updated',
            errorMessage: 'Could not update teams',
            selectAll: useSelectAllMode,
            filtersPayload,
            search: (appliedSearchQuery ?? '').trim(),
            excludeShipmentIds: useSelectAllMode ? excludedIds : [],
            countForUndo: selectedCount,
            buildPayload: () => {
              if (!useSelectAllMode) {
                return {
                  action: 'update_teams',
                  shipment_ids: selectedIds,
                  add_team_ids,
                  remove_team_ids,
                };
              }
              const { filters, start, end, view_by } =
                splitCalendarScopedFilters(filtersPayload);
              return {
                action: 'update_teams',
                select_all: true,
                exclude_shipment_ids: excludedIds,
                filters: {
                  ...filters,
                  search: (appliedSearchQuery ?? '').trim(),
                },
                ...(start ? { start } : {}),
                ...(end ? { end } : {}),
                ...(view_by ? { view_by } : {}),
                add_team_ids,
                remove_team_ids,
              };
            },
            onSuccess: () => {
              resetSelection();
              refresh();
            },
          });
        }}
      />

      <CustomDeleteModal
        visible={showCOCConfirmationModal}
        onClose={() => {
          setShowCOCConfirmationModal(false);
          // Don't reopen main menu when modal is closed
        }}
        onConfirm={handleExportCOC}
        title={`Export ${themeT('chainOfCustody', theme.themeType).toLowerCase()}`}
        message={getCOCModalMessage({
          selectedShipments: cOCExportCounts.selected_shipments || 0,
          statusUpdateCount: cOCExportCounts?.status_pdf_count ?? 0,
          podCount: cOCExportCounts?.pod_pdf_count ?? 0,
          shipments_with_pod: cOCExportCounts?.shipments_with_pod ?? 0,
          userEmail: user?.email,
          exportStatusUpdate: exportItemsState?.statusUpdates,
          exportPODs: exportItemsState?.pods,
          themeText: `${themeT('statusUpdates', theme.themeType)?.toLowerCase()}`,
        })}
        cancelText='Cancel'
        confirmText={finalCount > 200 ? 'Email me the link' : 'Download'}
        confirmColor='var(--text-primary)'
      />
    </div>
  );
};

export default ShipmentToolbar;
